import { useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { BookOpen, Loader2 } from 'lucide-react'

import { supabase, toAppError } from '@/lib/db/supabase'
import { useAuth } from '@/lib/auth/use-auth'
import { ORG_STORAGE_KEY } from '@/lib/org/org-context'
import { fiscalYearOf, today } from '@/lib/date/fiscal-year'
import { cn } from '@/lib/utils'
import { borderClassName, controlClassName } from '@/components/form/FormField'

/**
 * First-run workspace creation.
 *
 * Shown to a signed-in user who belongs to no organization yet. One RPC
 * (`create_organization`) creates the org, makes the caller its owner, and
 * seeds the full Indian chart of accounts, default categories and GST rates —
 * so the person goes from a bare login to a working set of books in one step,
 * without ever touching the SQL editor.
 */
export default function OnboardingPage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const currentFy = fiscalYearOf(today())

  const [name, setName] = useState('')
  const [gstin, setGstin] = useState('')
  // Default the books to the start of the current financial year — the most
  // common choice, and the one that makes "This Financial Year" work on day one.
  const [bookStart, setBookStart] = useState(currentFy.start)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (name.trim() === '') return

    setError(null)
    setSubmitting(true)
    try {
      const { data, error: rpcError } = await supabase.rpc('create_organization', {
        p_name: name.trim(),
        p_book_start_date: bookStart,
        ...(gstin.trim() ? { p_gstin: gstin.trim().toUpperCase() } : {}),
      })
      if (rpcError) throw new Error(toAppError(rpcError).message)

      // Point the org provider at the workspace we just made, then refetch
      // membership so the app transitions straight into it.
      if (typeof data === 'string') {
        try {
          localStorage.setItem(ORG_STORAGE_KEY, data)
        } catch {
          /* preference will not persist; the session still works */
        }
      }
      await queryClient.invalidateQueries({ queryKey: ['organizations', user?.id ?? null] })
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not create the workspace.')
      setSubmitting(false)
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center gap-2">
          <BookOpen className="size-7 text-accent" aria-hidden />
          <span className="text-xl font-semibold tracking-tight text-fg">
            Company <span className="text-fg-muted">Balance Sheet</span>
          </span>
        </div>

        <div className="rounded-card border border-border bg-surface p-6 shadow-sm">
          <h1 className="text-lg font-semibold text-fg">Create your workspace</h1>
          <p className="mt-1 text-sm text-fg-secondary">
            This sets up your chart of accounts, categories and GST rates. You can change any of it
            later in Settings.
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" noValidate>
            <div>
              <label htmlFor="org-name" className="mb-1.5 block text-sm font-medium text-fg">
                Company name<span className="ml-0.5 text-danger">*</span>
              </label>
              <input
                id="org-name"
                type="text"
                required
                autoFocus
                value={name}
                onChange={(event) => setName(event.target.value)}
                disabled={submitting}
                placeholder="Space2Code"
                className={cn(controlClassName, borderClassName(false))}
              />
            </div>

            <div>
              <label htmlFor="org-gstin" className="mb-1.5 block text-sm font-medium text-fg">
                GSTIN <span className="text-fg-muted">(optional)</span>
              </label>
              <input
                id="org-gstin"
                type="text"
                value={gstin}
                onChange={(event) => setGstin(event.target.value)}
                disabled={submitting}
                placeholder="27ABCDE1234F1Z5"
                maxLength={15}
                className={cn(controlClassName, borderClassName(false), 'uppercase')}
              />
            </div>

            <div>
              <label htmlFor="book-start" className="mb-1.5 block text-sm font-medium text-fg">
                Books start from
              </label>
              <input
                id="book-start"
                type="date"
                value={bookStart}
                onChange={(event) => setBookStart(event.target.value)}
                disabled={submitting}
                className={cn(controlClassName, borderClassName(false))}
              />
              <p className="mt-1.5 text-xs text-fg-muted">
                No voucher can be dated before this. Defaults to the start of {currentFy.label}.
              </p>
            </div>

            {error && (
              <p role="alert" className="rounded-control bg-danger-subtle px-3 py-2 text-sm text-danger">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || name.trim() === ''}
              className={cn(
                'flex h-10 w-full items-center justify-center gap-2 rounded-control',
                'bg-accent text-sm font-medium text-fg-on-accent transition-colors',
                'hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60',
              )}
            >
              {submitting && <Loader2 className="size-4 animate-spin" aria-hidden />}
              {submitting ? 'Setting up…' : 'Create workspace'}
            </button>
          </form>
        </div>

        <p className="mt-6 text-center text-xs text-fg-muted">Signed in as {user?.email}</p>
      </div>
    </div>
  )
}
